import { z } from 'zod';

/**
 * 환경변수 스키마. 브라우저(`env.ts`)와 빌드(`vite.config.ts`)가 같은 스키마로 검증한다.
 * 변수를 늘릴 때는 X4(env-contract.yaml)를 먼저 고친다.
 */
const emptyToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value;

export const envSchema = z.object({
  VITE_APP_ENV: z.preprocess(
    emptyToUndefined,
    z.enum(['local', 'dev', 'staging', 'prod']).default('local'),
  ),
  VITE_LOG_LEVEL: z.preprocess(
    emptyToUndefined,
    z.enum(['debug', 'info', 'warn', 'error', 'silent']).default('warn'),
  ),
  VITE_ERROR_REPORT_DSN: z.preprocess(emptyToUndefined, z.url().optional()),
  VITE_ANALYTICS_KEY: z.preprocess(emptyToUndefined, z.string().min(1).optional()),
});

export type Env = z.infer<typeof envSchema>;

export class EnvValidationError extends Error {
  constructor(readonly issues: readonly string[]) {
    super(`환경변수가 올바르지 않습니다.\n${issues.map((line) => `  - ${line}`).join('\n')}`);
    this.name = 'EnvValidationError';
  }
}

/** 값은 메시지에 넣지 않는다(이름과 이유만). */
export function parseEnv(source: Record<string, unknown>): Env {
  const result = envSchema.safeParse(source);
  if (result.success) return result.data;
  throw new EnvValidationError(
    result.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`),
  );
}
